import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, Clock, Navigation, Store, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { GlassCard, SectionTitle } from "../components/common/ui";
import { Button } from "../components/ui/button";
import AgeBadge from "../components/common/AgeBadge";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import api from "../lib/api";

const INTERNAL_LINKS = [
  ["Browse Fireworks", "/fireworks"],
  ["Offers", "/offers"],
  ["Click & Collect", "/click-and-collect"],
  ["Firework Safety", "/safety"],
  ["Age Policy", "/age-policy"],
];

function CollectionPoint({ store }) {
  if (!store) return null;
  const addressLines = [
    store.address_line1, store.address_line2,
    [store.town, store.city_county].filter(Boolean).join(", "),
    store.postcode,
  ].filter(Boolean);
  const logos = [store.primary_logo, store.secondary_logo].filter(Boolean);
  const services = (store.services || []).filter((s) => s && s.enabled !== false && s.name);

  return (
    <GlassCard className="p-6 sm:p-8" hover={false} data-testid="areas-collection-point">
      <div className="flex items-center gap-2 text-gold">
        <Store className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wide">Your Collection Point</span>
      </div>

      {logos.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="areas-store-logos">
          {logos.map((logo, i) => (
            <span key={i} className="inline-flex items-center justify-center rounded-lg bg-white/90 p-2">
              <img src={logo} alt={`${store.name} logo`} className="h-12 w-auto max-w-[120px] object-contain" loading="lazy" />
            </span>
          ))}
        </div>
      )}

      <h2 className="mt-4 font-playfair text-2xl text-white">{store.name}</h2>

      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        <div className="space-y-3 text-sm text-white/75">
          {addressLines.length > 0 && (
            <p className="flex items-start gap-2" data-testid="areas-store-address">
              <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" />
              <span>{addressLines.map((l, i) => <span key={i} className="block">{l}</span>)}</span>
            </p>
          )}
          {store.opening_hours_display?.length > 0 && (
            <p className="flex items-start gap-2" data-testid="areas-store-hours">
              <Clock className="h-4 w-4 text-gold mt-0.5 shrink-0" />
              <span>{store.opening_hours_display.map((h, i) => <span key={i} className="block">{h}</span>)}</span>
            </p>
          )}
        </div>

        {services.length > 0 && (
          <div data-testid="areas-store-services">
            <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Services here</p>
            <div className="flex flex-wrap items-center gap-2">
              {services.map((sv, i) => (
                sv.image ? (
                  <span key={i} title={sv.name} className="inline-flex items-center justify-center rounded-md bg-white/90 p-1.5">
                    <img src={sv.image} alt={sv.name} className="h-7 w-auto max-w-[80px] object-contain" loading="lazy" />
                  </span>
                ) : (
                  <span key={i} className="inline-flex items-center rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[11px] text-white/70">{sv.name}</span>
                )
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {store.directions_url && (
          <a href={store.directions_url} target="_blank" rel="noopener noreferrer" data-testid="areas-directions-btn">
            <Button className="cf-gold-btn font-semibold gap-2"><Navigation className="h-4 w-4" /> Get Directions</Button>
          </a>
        )}
        <Link to="/click-and-collect" data-testid="areas-clickcollect-btn">
          <Button variant="outline" className="border-white/20 text-white gap-2"><Sparkles className="h-4 w-4" /> Click &amp; Collect</Button>
        </Link>
        {store.slug && (
          <Link to={`/stores/${store.slug}`} data-testid="areas-store-page-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:opacity-90">
            View store details <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-white/10 pt-5" data-testid="areas-age-notice">
        <AgeBadge size={40} linked />
        <span className="text-sm text-white/50">18+ only · Challenge 25 applies. Please bring valid photo ID when you collect.</span>
      </div>
    </GlassCard>
  );
}

export default function AreasWeServe() {
  const [groups, setGroups] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Areas We Serve | Clacton Fireworks";
    Promise.all([
      api.get("/areas").then(({ data }) => data).catch(() => []),
      api.get("/stores/primary").then(({ data }) => data).catch(() => null),
    ]).then(([a, s]) => { setGroups(a || []); setStore(s); }).finally(() => setLoading(false));
    return () => { document.title = "Clacton Fireworks"; };
  }, []);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20" data-testid="areas-we-serve-page">
      <Breadcrumbs className="mb-6" />
      <h1 className="font-playfair text-4xl sm:text-5xl text-white">Areas We Serve</h1>
      <p className="mt-5 max-w-3xl text-base text-white/75 leading-relaxed" data-testid="areas-intro">
        Clacton Fireworks proudly serves customers across Weeley, Clacton-on-Sea, Tendring, North Essex and
        surrounding communities. Customers can browse and reserve fireworks online, then collect from our
        convenient local collection point at Weeley Post Office &amp; Store.
      </p>

      <div className="mt-10">
        <CollectionPoint store={store} />
      </div>

      <div className="mt-16">
        <SectionTitle eyebrow="Local catchment" title="Towns & villages we serve"
          subtitle="Reserve online for collection at Weeley — we welcome customers from across these areas." />
        {groups.length === 0 ? (
          <GlassCard className="mt-8 p-10 text-center text-muted-foreground" hover={false}>
            Area details are being finalised — please check back soon.
          </GlassCard>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="areas-groups">
            {groups.map((g) => (
              <GlassCard key={g.group_id} className="p-6 h-full flex flex-col" data-testid={`area-group-${g.slug}`}>
                <h3 className="font-playfair text-xl text-white">{g.name}</h3>
                {g.intro && <p className="mt-2 text-sm text-white/55">{g.intro}</p>}
                <ul className="mt-4 flex flex-wrap gap-2" data-testid={`area-group-locations-${g.slug}`}>
                  {g.locations.map((l) => (
                    <li key={l.location_id}>
                      {l.map_url ? (
                        <a href={l.map_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-white/80 hover:border-gold/40 hover:text-gold transition-colors"
                          title={l.note || l.name} data-testid={`area-location-${l.location_id}`}>
                          {l.name}
                        </a>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-white/80"
                          title={l.note || undefined} data-testid={`area-location-${l.location_id}`}>
                          {l.name}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            ))}
          </div>
        )}
      </div>

      <div className="mt-16">
        <h2 className="font-playfair text-2xl text-white">Explore more</h2>
        <div className="mt-5 flex flex-wrap gap-3" data-testid="areas-internal-links">
          {INTERNAL_LINKS.map(([label, to]) => (
            <Link key={to} to={to} data-testid={`areas-link-${to.replace(/[^a-z]+/gi, "-")}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-white/85 hover:border-gold/40 hover:text-gold transition-colors">
              {label} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ))}
          {store?.slug && (
            <Link to={`/stores/${store.slug}`} data-testid="areas-link-store"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-white/85 hover:border-gold/40 hover:text-gold transition-colors">
              {store.name} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
